import React, { useState, useEffect, Fragment } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import StatCard from '../common/StatCard';
import {
  CurrencyRupeeIcon,
  ShoppingCartIcon,
  TruckIcon,
  ArrowTrendingUpIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon,
  BanknotesIcon,
  ShoppingBagIcon,
  DocumentChartBarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import CreateOrder from '../sales/CreateOrder';
import CreatePurchase from '../purchases/CreatePurchase';
import AddExpenseModal from '../transportation/AddExpenseModal';
import { PDFDownloadButton as SalesPDFButton } from '../sales/SalesReports';
import { PDFDownloadButton as PurchasePDFButton } from '../purchases/PurchaseReports';
import { Dialog, Transition } from '@headlessui/react';
import { doc, getDoc } from 'firebase/firestore';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon } from '@heroicons/react/24/outline';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DatePickerInput = ({ value, onClick }) => (
  <button
    onClick={onClick}
    type="button"
    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
  >
    <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" aria-hidden="true" />
    {value || 'Select date'}
  </button>
);

export default function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalPurchases: 0,
    totalExpenses: 0,
    netProfit: 0,
    totalOrders: 0,
    pendingPayments: 0,
    payablesToSuppliers: 0
  });

  // Additional stats calculations
  const [detailedStats, setDetailedStats] = useState({
    topSuppliers: [],
    averageOrderValue: 0,
    averagePurchaseValue: 0,
    totalSuppliers: 0,
    totalCustomers: 0,
    transportCosts: 0,
    wagesExpenses: 0,
    otherExpenses: 0
  });

  // Add new state for period tracking
  const [periodStats, setPeriodStats] = useState({
    netProfit: { period: 'month', value: 0 },
    totalRevenue: { period: 'month', value: 0 },
    totalExpenses: { period: 'month', value: 0 }
  });

  // Add this new state for revenue chart period
  const [revenuePeriod, setRevenuePeriod] = useState('week');

  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showCreatePurchase, setShowCreatePurchase] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showSalesReport, setShowSalesReport] = useState(false);
  const [showPurchaseReport, setShowPurchaseReport] = useState(false);

  // Add these state variables and calculations at the top with other states
  const [uniqueCustomers, setUniqueCustomers] = useState([]);
  const [uniqueSuppliers, setUniqueSuppliers] = useState([]);

  // Add this useEffect to calculate unique customers and suppliers
  useEffect(() => {
    if (orders?.length) {
      const customers = [...new Set(orders
        .filter(order => order && order.customerName)
        .map(order => order.customerName))]
        .filter(Boolean)
        .sort();
      setUniqueCustomers(customers);
    }

    if (purchases?.length) {
      const suppliers = [...new Set(purchases
        .filter(purchase => purchase && purchase.supplierName)
        .map(purchase => purchase.supplierName))]
        .filter(Boolean)
        .sort();
      setUniqueSuppliers(suppliers);
    }
  }, [orders, purchases]);

  // Add function to calculate period stats
  const calculatePeriodStats = (period, statType) => {
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date(now);

    switch (period) {
      case 'day':
        // Set to start of today
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        // Set to start of current week (Sunday)
        startDate = new Date(now);
        startDate.setDate(now.getDate() - now.getDay());
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        // Set to start of current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      default:
        break;
    }

    // Filter orders for current period
    const periodOrders = orders.filter(order => {
      if (!order.finalizedAt) return false;
      const orderDate = new Date(order.finalizedAt);
      return orderDate >= startDate && orderDate <= endDate && order.status === 'completed';
    });

    // Filter purchases for current period
    const periodPurchases = purchases.filter(purchase => {
      if (!purchase.date) return false;
      const purchaseDate = purchase.date?.seconds 
        ? new Date(purchase.date.seconds * 1000)
        : new Date(purchase.date);
      return purchaseDate >= startDate && purchaseDate <= endDate;
    });

    // Filter expenses for current period
    const periodExpenses = expenses.filter(expense => {
      if (!expense.date) return false;
      // Convert YYYY-MM-DD string to Date object
      const [year, month, day] = expense.date.split('-').map(Number);
      const expenseDate = new Date(year, month - 1, day); // month is 0-based in Date constructor
      const expenseStartOfDay = new Date(expenseDate.setHours(0, 0, 0, 0));
      
      return expenseStartOfDay >= startDate && expenseStartOfDay <= endDate;
    });

    const revenue = periodOrders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
    const purchaseTotal = periodPurchases.reduce((sum, purchase) => sum + (purchase.totalAmount || 0), 0);
    const expenseTotal = periodExpenses.reduce((sum, expense) => sum + Number(expense.amount), 0);
    const totalExpenses = purchaseTotal + expenseTotal;
    const netProfit = revenue - totalExpenses;

    setPeriodStats(prev => ({
      ...prev,
      [statType]: {
        period,
        value: statType === 'netProfit' ? netProfit :
          statType === 'totalRevenue' ? revenue :
            totalExpenses
      }
    }));
  };

  // Initialize period stats
  useEffect(() => {
    if (orders.length || purchases.length || expenses.length) {
      calculatePeriodStats('month', 'netProfit');
      calculatePeriodStats('month', 'totalRevenue');
      calculatePeriodStats('month', 'totalExpenses');
    }
  }, [orders, purchases, expenses]);

  // Fetch data from Firebase
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch completed orders
        const ordersQuery = query(
          collection(db, 'orders'),
          where('status', '==', 'completed')
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        const ordersData = ordersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Fetch purchases
        const purchasesSnapshot = await getDocs(collection(db, 'purchases'));
        const purchasesData = purchasesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Fetch expenses
        const expensesSnapshot = await getDocs(collection(db, 'expenses'));
        const expensesData = expensesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setOrders(ordersData);
        setPurchases(purchasesData);
        setExpenses(expensesData);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate stats when data changes
  useEffect(() => {
    if (!orders.length && !purchases.length && !expenses.length) return;

    // Calculate total revenue
    const revenue = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);

    // Calculate total purchases
    const purchaseTotal = purchases.reduce((sum, purchase) => sum + (purchase.totalAmount || 0), 0);

    // Calculate total expenses
    const expenseTotal = expenses.reduce((sum, expense) => sum + Number(expense.amount), 0);

    // Calculate pending payments (receivables)
    const pending = orders.reduce((sum, order) => {
      const remaining = order.totalAmount - (order.amountReceived || 0);
      return sum + (remaining > 0 ? remaining : 0);
    }, 0);

    // Calculate payables to suppliers
    const payables = purchases.reduce((sum, purchase) => {
      const remaining = purchase.totalAmount - (purchase.amountPaid || 0);
      return sum + (remaining > 0 ? remaining : 0);
    }, 0);

    // Calculate net profit
    const netProfit = revenue - purchaseTotal - expenseTotal;

    setStats({
      totalRevenue: revenue,
      totalPurchases: purchaseTotal,
      totalExpenses: expenseTotal,
      netProfit,
      totalOrders: orders.length,
      pendingPayments: pending,
      payablesToSuppliers: payables
    });
  }, [orders, purchases, expenses]);

  // Additional stats calculations
  useEffect(() => {
    if (!orders.length && !purchases.length && !expenses.length) return;

    // Calculate average values
    const avgOrder = stats.totalRevenue / orders.length || 0;
    const avgPurchase = stats.totalPurchases / purchases.length || 0;

    // Calculate expense categories
    const transportCosts = expenses
      .filter(expense => expense.category === 'fuel')
      .reduce((sum, expense) => sum + Number(expense.amount), 0);

    const wagesExpenses = expenses
      .filter(expense => expense.category === 'wages')
      .reduce((sum, expense) => sum + Number(expense.amount), 0);

    const otherExpenses = expenses
      .filter(expense => !['fuel', 'wages'].includes(expense.category))
      .reduce((sum, expense) => sum + Number(expense.amount), 0);

    // Get unique suppliers and customers
    const uniqueSuppliers = new Set(purchases.map(p => p.supplierId));
    const uniqueCustomers = new Set(orders.map(o => o.customerId));

    setDetailedStats({
      topSuppliers: [],
      averageOrderValue: avgOrder,
      averagePurchaseValue: avgPurchase,
      totalSuppliers: uniqueSuppliers.size,
      totalCustomers: uniqueCustomers.size,
      transportCosts: transportCosts,
      wagesExpenses: wagesExpenses,
      otherExpenses: otherExpenses
    });
  }, [orders, purchases, expenses, stats]);

  // Chart data preparation
  const monthlyData = calculateMonthlyData(orders, purchases, expenses);

  const lineChartData = {
    labels: monthlyData.map(d => d.month),
    datasets: [
      {
        label: 'Revenue',
        data: monthlyData.map(d => d.revenue),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Expenses',
        data: monthlyData.map(d => d.expenses + d.purchases),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4,
        fill: true,
      }
    ],
  };

  const barChartData = {
    labels: ['Vegetables', 'Transport', 'Wages', 'Other'],
    datasets: [
      {
        label: 'Expenses Distribution',
        data: calculateExpenseDistribution(expenses),
        backgroundColor: [
          'rgb(59, 130, 246)',
          'rgb(34, 197, 94)',
          'rgb(239, 68, 68)',
          'rgb(168, 85, 247)'
        ],
        borderRadius: 4,
      }
    ],
  };

  // Update the calculateDailyRevenue function to handle different periods
  function calculateRevenueData(orders, period) {
    let days;
    let format;

    switch (period) {
      case 'year':
        days = 365;
        format = { month: 'short', year: 'numeric' };
        break;
      case 'month':
        days = 30;
        format = { month: 'short', day: 'numeric' };
        break;
      case 'week':
      default:
        days = 7;
        format = { weekday: 'short', day: 'numeric' };
        break;
    }

    const dates = Array.from({ length: days }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - i);
      return d;
    }).reverse();

    const revenueData = dates.map(date => {
      const dayRevenue = orders
        .filter(order => {
          const orderDate = new Date(order.finalizedAt);
          if (period === 'year') {
            return orderDate.getMonth() === date.getMonth() &&
              orderDate.getFullYear() === date.getFullYear();
          }
          return orderDate.toDateString() === date.toDateString();
        })
        .reduce((sum, order) => sum + order.totalAmount, 0);

      return {
        date: date.toLocaleDateString('en-US', format),
        revenue: dayRevenue
      };
    });

    // For yearly data, aggregate by month
    if (period === 'year') {
      const monthlyData = {};
      revenueData.forEach(item => {
        if (!monthlyData[item.date]) {
          monthlyData[item.date] = 0;
        }
        monthlyData[item.date] += item.revenue;
      });
      return {
        labels: Object.keys(monthlyData),
        datasets: [{
          label: 'Revenue',
          data: Object.values(monthlyData),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
        }]
      };
    }

    return {
      labels: revenueData.map(d => d.date),
      datasets: [{
        label: 'Revenue',
        data: revenueData.map(d => d.revenue),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      }]
    };
  }


  return (
    <div className="space-y-6">
      {/* Primary Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <StatCard
          title="Net Profit/Loss"
          value={`₹${periodStats.netProfit.value.toLocaleString()}`}
          icon={ArrowTrendingUpIcon}
          iconBgColor={periodStats.netProfit.value > 0 ? 'bg-green-500' : 'bg-red-500'}
          borderColor={periodStats.netProfit.value > 0 ? 'border-green-500' : 'border-red-500'}
          hoverBgColor={periodStats.netProfit.value > 0 ? 'hover:bg-green-500' : 'hover:bg-red-500'}
          textColor={periodStats.netProfit.value > 0 ? 'text-green-500' : 'text-red-500'}
          showPeriodToggle={true}
          periodType="netProfit"
          currentPeriod={periodStats.netProfit.period}
          onPeriodChange={calculatePeriodStats}
        />
        <StatCard
          title="Total Revenue"
          value={`₹${periodStats.totalRevenue.value.toLocaleString()}`}
          icon={CurrencyRupeeIcon}
          iconBgColor="bg-blue-500"
          borderColor="border-blue-500"
          hoverBgColor="hover:bg-blue-500"
          textColor="text-blue-500"
          showPeriodToggle={true}
          periodType="totalRevenue"
          currentPeriod={periodStats.totalRevenue.period}
          onPeriodChange={calculatePeriodStats}
        />
        <StatCard
          title="Total Expenses"
          value={`₹${periodStats.totalExpenses.value.toLocaleString()}`}
          icon={BuildingStorefrontIcon}
          iconBgColor="bg-yellow-500"
          borderColor="border-yellow-500"
          hoverBgColor="hover:bg-yellow-500"
          textColor="text-yellow-500"
          showPeriodToggle={true}
          periodType="totalExpenses"
          currentPeriod={periodStats.totalExpenses.period}
          onPeriodChange={calculatePeriodStats}
        />

      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <StatCard
          title="Receivables"
          value={`₹${stats.pendingPayments.toLocaleString()}`}
          icon={ArchiveBoxIcon}
          iconBgColor="bg-red-500"
          borderColor="border-red-500"
          hoverBgColor="hover:bg-red-500"
          textColor="text-red-500"
        />
        <StatCard
          title="Payables"
          value={`₹${stats.payablesToSuppliers.toLocaleString()}`}
          icon={ShoppingCartIcon}
          iconBgColor="bg-purple-500"
          borderColor="border-purple-500"
          hoverBgColor="hover:bg-purple-500"
          textColor="text-purple-500"
        />
        <StatCard
          title="Total Transport Cost"
          value={`₹${stats.totalExpenses.toLocaleString()}`}
          icon={BanknotesIcon}
          iconBgColor="bg-teal-500"
          borderColor="border-teal-500"
          hoverBgColor="hover:bg-teal-500"
          textColor="text-teal-500"
        />
      </div>

      {/* Quick Actions Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create New Card */}
        <div className="bg-white dark:bg-[#2E3B4E] rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 dark:text-white">Create New</h3>
          <div className="flex flex-col space-y-4">
            <button
              onClick={() => setShowCreateOrder(true)}
              className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md 
                text-white bg-blue-600 hover:bg-blue-700 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ShoppingCartIcon className="h-5 w-5 mr-2" />
              Create Order
            </button>
            
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setShowCreatePurchase(true)}
                className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md 
                  text-white bg-green-600 hover:bg-green-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <ShoppingBagIcon className="h-5 w-5 mr-2" />
                Create Purchase
              </button>

              <button
                onClick={() => setShowAddExpense(true)}
                className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md 
                  text-white bg-yellow-600 hover:bg-yellow-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <BanknotesIcon className="h-5 w-5 mr-2" />
                Add Transport Cost
              </button>
            </div>
          </div>
        </div>

        {/* Reports Card */}
        <div className="bg-white dark:bg-[#2E3B4E] rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 dark:text-white">Generate Reports</h3>
          <div className="flex flex-col space-y-4">
            <button
              onClick={() => setShowSalesReport(true)}
              className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md 
                text-white bg-purple-600 hover:bg-purple-700 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <DocumentChartBarIcon className="h-5 w-5 mr-2" />
              Sales Report
            </button>
            
            <button
              onClick={() => setShowPurchaseReport(true)}
              className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md 
                text-white bg-indigo-600 hover:bg-indigo-700 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <DocumentChartBarIcon className="h-5 w-5 mr-2" />
              Purchase Report
            </button>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Selling Vegetables */}
        <div className="bg-white dark:bg-[#2E3B4E] rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 dark:text-white">Top Selling Vegetables</h3>
          <div style={{ height: '400px' }}>
            <Bar
              data={calculateTopSellingVegetables(orders)}
              options={{
                ...chartOptions,
                indexAxis: 'y',
                plugins: {
                  ...chartOptions.plugins,
                  title: {
                    display: true,
                    text: 'By Quantity Sold (kg)'
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Revenue Trend */}
        <div className="bg-white dark:bg-[#2E3B4E] rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold dark:text-white">Revenue Trend</h3>
            <div className="flex space-x-1">
              <button
                onClick={() => setRevenuePeriod('week')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${revenuePeriod === 'week'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
              >
                Week
              </button>
              <button
                onClick={() => setRevenuePeriod('month')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${revenuePeriod === 'month'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
              >
                Month
              </button>
              <button
                onClick={() => setRevenuePeriod('year')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${revenuePeriod === 'year'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
              >
                Year
              </button>
            </div>
          </div>
          <div style={{ height: '400px' }}>
            <Line
              data={calculateRevenueData(orders, revenuePeriod)}
              options={{
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  title: {
                    display: true,
                    text: `Revenue Trend (${revenuePeriod.charAt(0).toUpperCase() + revenuePeriod.slice(1)})`
                  }
                },
                scales: {
                  ...chartOptions.scales,
                  y: {
                    ...chartOptions.scales.y,
                    ticks: {
                      callback: (value) => `₹${value.toLocaleString()}`,
                      color: 'rgb(156, 163, 175)',
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Expense Distribution */}
        <div className="lg:col-span-2 bg-white dark:bg-[#2E3B4E] rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 dark:text-white">Expense Distribution</h3>
          <div style={{ height: '400px' }}>
            <Bar
              data={calculateExpenseDistributionforChart(expenses, purchases)}
              options={{
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  title: {
                    display: true,
                    text: 'By Category'
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      <CreateOrder 
        isOpen={showCreateOrder}
        onClose={() => setShowCreateOrder(false)}
        onOrderCreated={() => {
          setShowCreateOrder(false);
        }}
      />

      <CreatePurchase
        isOpen={showCreatePurchase}
        onClose={() => setShowCreatePurchase(false)}
        onPurchaseCreated={() => {
          setShowCreatePurchase(false);
        }}
      />

      <AddExpenseModal
        isOpen={showAddExpense}
        onClose={() => setShowAddExpense(false)}
        vehicles={[]} // You'll need to fetch vehicles data
        onSubmit={() => {
          setShowAddExpense(false);
        }}
      />

      <GenerateReportModal
        isOpen={showSalesReport}
        onClose={() => setShowSalesReport(false)}
        title="Generate Sales Report"
        options={uniqueCustomers}
        type="sales"
        orders={orders}
        purchases={purchases}
      />

      <GenerateReportModal
        isOpen={showPurchaseReport}
        onClose={() => setShowPurchaseReport(false)}
        title="Generate Purchase Report"
        options={uniqueSuppliers}
        type="purchases"
        orders={orders}
        purchases={purchases}
      />
    </div>
  );
}

// Helper functions
function calculateMonthlyData(orders, purchases, expenses) {
  const monthlyData = [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Get date range from data
  const dates = [
    ...orders.map(order => new Date(order.finalizedAt)),
    ...purchases.map(purchase => new Date(purchase.date.seconds * 1000)),
    ...expenses.map(expense => new Date(expense.date))
  ].filter(date => !isNaN(date));

  if (dates.length === 0) return [];

  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  // Create monthly buckets
  let currentDate = new Date(minDate);
  currentDate.setDate(1); // Start from beginning of month

  while (currentDate <= maxDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Calculate revenue from orders
    const monthlyRevenue = orders
      .filter(order => {
        const orderDate = new Date(order.finalizedAt);
        return orderDate.getMonth() === month &&
          orderDate.getFullYear() === year &&
          order.status === 'completed';
      })
      .reduce((sum, order) => sum + (order.totalAmount || 0), 0);

    // Calculate expenses from purchases
    const monthlyPurchases = purchases
      .filter(purchase => {
        const purchaseDate = new Date(purchase.date.seconds * 1000);
        return purchaseDate.getMonth() === month &&
          purchaseDate.getFullYear() === year;
      })
      .reduce((sum, purchase) => sum + (purchase.totalAmount || 0), 0);

    // Calculate other expenses
    const monthlyExpenses = expenses
      .filter(expense => {
        const expenseDate = new Date(expense.date);
        return expenseDate.getMonth() === month &&
          expenseDate.getFullYear() === year;
      })
      .reduce((sum, expense) => sum + Number(expense.amount || 0), 0);

    monthlyData.push({
      month: `${monthNames[month]} ${year}`,
      revenue: monthlyRevenue,
      purchases: monthlyPurchases,
      expenses: monthlyExpenses
    });

    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return monthlyData;
}

function calculateExpenseDistribution(expenses) {
  const categories = {
    vegetables: 0,  // From purchases
    transport: 0,   // Fuel expenses
    wages: 0,       // Driver wages
    other: 0        // Other expenses like food
  };

  expenses.forEach(expense => {
    switch (expense.category) {
      case 'fuel':
        categories.transport += Number(expense.amount);
        break;
      case 'wages':
        categories.wages += Number(expense.amount);
        break;
      case 'food':
        categories.other += Number(expense.amount);
        break;
      default:
        categories.other += Number(expense.amount);
    }
  });

  return Object.values(categories);
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        usePointStyle: true,
        color: 'rgb(156, 163, 175)',
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: 'rgb(156, 163, 175)',
      },
    },
    y: {
      grid: {
        color: 'rgba(156, 163, 175, 0.1)',
      },
      ticks: {
        color: 'rgb(156, 163, 175)',
      },
    },
  },
};

// 1. Top selling vegetables by quantity
function calculateTopSellingVegetables(orders) {
  const vegetableSales = {};

  orders.forEach(order => {
    order.items.forEach(item => {
      const vegName = item.vegetable;
      if (!vegetableSales[vegName]) {
        vegetableSales[vegName] = 0;
      }
      vegetableSales[vegName] += Number(item.actualQuantity);
    });
  });

  // Convert to array and sort
  const sortedSales = Object.entries(vegetableSales)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return {
    labels: sortedSales.map(([name]) => name),
    datasets: [{
      label: 'Quantity Sold (kg)',
      data: sortedSales.map(([, qty]) => qty),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(217, 119, 6, 0.8)',
        'rgba(139, 92, 246, 0.8)',
      ],
      borderRadius: 4,
    }]
  };
}

// 3. Expense Distribution
function calculateExpenseDistributionforChart(expenses, purchases) {
  const categories = {
    purchases: purchases.reduce((sum, p) => sum + (p.totalAmount || 0), 0),
    fuel: expenses.filter(e => e.category === 'fuel').reduce((sum, e) => sum + Number(e.amount), 0),
    wages: expenses.filter(e => e.category === 'wages').reduce((sum, e) => sum + Number(e.amount), 0),
    food: expenses.filter(e => e.category === 'food').reduce((sum, e) => sum + Number(e.amount), 0),
    other: expenses.filter(e => !['fuel', 'wages', 'food'].includes(e.category)).reduce((sum, e) => sum + Number(e.amount), 0)
  };

  return {
    labels: ['Purchases', 'Fuel', 'Wages', 'Food', 'Other'],
    datasets: [{
      label: 'Expense Distribution',
      data: Object.values(categories),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(217, 119, 6, 0.8)',
        'rgba(139, 92, 246, 0.8)',
      ],
      borderRadius: 4,
    }]
  };
}

const GenerateReportModal = ({ isOpen, onClose, title, type, options, orders, purchases }) => {
  const [selectedEntity, setSelectedEntity] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [businessDetails, setBusinessDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchBusinessDetails = async () => {
      const docRef = doc(db, 'settings', 'business');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setBusinessDetails(docSnap.data());
      }
    };
    fetchBusinessDetails();
  }, []);

  // Get the appropriate data based on type
  const data = type === 'sales' ? orders : purchases;

  const handleGenerateReport = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      alert('Please select a date range');
      return;
    }

    setLoading(true);
    try {
      // Instead of hiding the buttons, we'll render them directly
      if (type === 'sales') {
        return (
          <SalesPDFButton
            orders={orders.filter(order => order.status === 'completed')}
            dateRange={dateRange}
            businessDetails={businessDetails}
            customerFilter={selectedEntity}
          />
        );
      } else {
        return (
          <PurchasePDFButton
            purchases={purchases}
            dateRange={dateRange}
            businessDetails={businessDetails}
            supplierFilter={selectedEntity}
          />
        );
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden bg-white dark:bg-gray-800 
                p-4 sm:p-6 text-left align-middle shadow-xl transition-all rounded-t-2xl sm:rounded-2xl">
                <div className="flex items-center justify-between mb-4 mt-6 pt-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    {title}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 sm:hidden"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {type === 'sales' ? 'Select Customer' : 'Select Supplier'}
                    </label>
                    <select
                      value={selectedEntity}
                      onChange={(e) => setSelectedEntity(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                        text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">All {type === 'sales' ? 'Customers' : 'Suppliers'}</option>
                      {options.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 mt-6 pt-6">
                      Date Range
                    </label>
                    <DatePicker
                      selectsRange={true}
                      startDate={dateRange.startDate}
                      endDate={dateRange.endDate}
                      onChange={(update) => setDateRange({ startDate: update[0], endDate: update[1] })}
                      customInput={<DatePickerInput />}
                      isClearable
                      className="w-full"
                    />
                  </div>

                  <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-gray-700 dark:text-gray-300 
                        bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                        hover:bg-gray-50 dark:hover:bg-gray-600 
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      Cancel
                    </button>

                    {/* Render PDF buttons directly */}
                    {type === 'sales' ? (
                      <SalesPDFButton
                        orders={orders.filter(order => order.status === 'completed')}
                        dateRange={dateRange}
                        businessDetails={businessDetails}
                        customerFilter={selectedEntity}
                        className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white 
                          bg-primary-600 rounded-lg border border-transparent
                          hover:bg-primary-700 
                          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent 
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Generating...' : 'Generate Sales Report'}
                      </SalesPDFButton>
                    ) : (
                      <PurchasePDFButton
                        purchases={purchases}
                        dateRange={dateRange}
                        businessDetails={businessDetails}
                        supplierFilter={selectedEntity}
                        className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white 
                          bg-primary-600 rounded-lg border border-transparent
                          hover:bg-primary-700 
                          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent 
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Generating...' : 'Generate Purchase Report'}
                      </PurchasePDFButton>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};